import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  Inbox,
  Kanban,
  Zap,
  Settings,
  Activity,
  DollarSign,
  ShoppingBag,
  Target,
  UserCircle2,
  Activity as ActivityPulse,
  Filter as FunnelIcon,
  Headphones,
  LogOut,
  MessageCircle,
} from 'lucide-react';
import { cn } from '../lib/utils.js';
import { apiClient } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  badgeKey?: 'recovery';
}

const navItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/meu-trabalho', label: 'Meu trabalho', icon: Headphones },
  { to: '/contatos', label: 'Contatos', icon: Users },
  { to: '/inbox', label: 'Inbox', icon: Inbox },
  { to: '/pipeline', label: 'Pipeline', icon: Kanban },
  { to: '/recuperacao', label: 'Recuperação', icon: Zap, badgeKey: 'recovery' },
  { to: '/vendas', label: 'Vendas', icon: ShoppingBag },
  { to: '/trafego', label: 'Tráfego', icon: Target },
  { to: '/funil', label: 'Funil', icon: FunnelIcon },
  { to: '/financeiro', label: 'Financeiro', icon: DollarSign },
  { to: '/equipe', label: 'Equipe', icon: UserCircle2 },
  { to: '/operacoes', label: 'Operações', icon: ActivityPulse },
];

export function Layout(): JSX.Element {
  const location = useLocation();
  const { user, logout, canAccessRoute } = useAuth();

  const { data: recoveryData } = useQuery({
    queryKey: ['recovery-queue-summary'],
    queryFn: () => apiClient.recoveryQueue({}),
    refetchInterval: 60_000,
    enabled: !!user,
  });
  const recoveryCount = recoveryData?.summary.total ?? 0;
  const recoveryHot = recoveryData?.summary.hotCount ?? 0;

  const visibleNav = navItems.filter((item) => canAccessRoute(item.to));
  const canSettings = canAccessRoute('/configuracoes');

  return (
    <div className="flex h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-border flex flex-col">
        <div className="px-4 py-4 border-b border-border">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="font-bold tracking-tight text-sm">NextBoot CRM</div>
              <div className="text-[10px] text-muted-foreground">Shadow Ledger · v0.0.1</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-muted text-foreground font-medium'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                  )
                }
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1">{item.label}</span>
                {item.badgeKey === 'recovery' && recoveryCount > 0 && (
                  <span
                    className={cn(
                      'shrink-0 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 rounded-full text-[10px] font-bold tabular-nums',
                      recoveryHot > 0
                        ? 'bg-rose-500 text-white animate-pulse'
                        : 'bg-amber-500/20 text-amber-300',
                    )}
                    title={`${recoveryCount} na fila · ${recoveryHot} 🔥`}
                  >
                    {recoveryCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border px-2 py-3 space-y-1">
          {user && (
            <div className="px-3 py-2 mb-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Logado como
              </div>
              <div className="text-xs font-medium truncate" title={user.email}>
                {user.fullName}
              </div>
              <div className="text-[10px] text-muted-foreground capitalize">
                {user.role.replace('_', ' ')}
              </div>
            </div>
          )}
          {canSettings && (
            <>
              <NavLink
                to="/configuracoes/whatsapp"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-muted text-foreground font-medium'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                  )
                }
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </NavLink>
              <NavLink
                to="/configuracoes"
                end
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-muted text-foreground font-medium'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                  )
                }
              >
                <Settings className="h-4 w-4" />
                Configurações
              </NavLink>
            </>
          )}
          <button
            type="button"
            onClick={logout}
            className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50 hover:text-rose-400 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sair
          </button>
          <div className="flex items-center gap-2 rounded-md px-3 py-2 text-xs text-muted-foreground">
            <Activity className="h-3 w-3 text-emerald-500" />
            <span>Operando</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto" key={location.pathname}>
        <Outlet />
      </main>
    </div>
  );
}
